import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import type { AzureImportModule } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const azureImportsSearchSchema = z.object({
  ...datatableSearchSchema.shape,
  type: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
})

type AzureImportsSearch = z.infer<typeof azureImportsSearchSchema>

/**
 * Each importing module mounts its own azure imports router on its own tables and queue. Only CRT
 * imports from Azure today; Credit becomes one more case here once it has a router.
 */
function getAzureImportsApi(module: AzureImportModule) {
  const api = getApi()

  switch (module) {
    case 'crt':
      return api.crt['azure-imports']
  }
}

async function getAzureImports(
  module: AzureImportModule,
  queries: AzureImportsSearch,
  signal?: AbortSignal,
) {
  const res = await getAzureImportsApi(module).$get(
    {
      query: stringifyValues(queries),
    },
    { init: { signal } },
  )

  const { azureImports, count } = await res.json()
  return { azureImports, count }
}

export type AzureImports = Awaited<ReturnType<typeof getAzureImports>>

export const useAzureImports = (module: AzureImportModule, queries: AzureImportsSearch) => {
  return useQuery({
    queryKey: ['azure-imports', module, queries],
    queryFn: async ({ signal }) => getAzureImports(module, queries, signal),
  })
}

async function retryAzureImport(module: AzureImportModule, id: number) {
  await getAzureImportsApi(module)[':id']['retry'].$post({
    param: { id: String(id) },
  })
}

export function useRetryAzureImport(module: AzureImportModule) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['uploads', 'common'])

  return useMutation({
    mutationKey: ['retry-azure-import', module],
    mutationFn: (id: number) => retryAzureImport(module, id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['azure-imports'] })
    },
    onSuccess: () => {
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('azureImports.retryQueued'),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('azureImports.retryFailed'),
        className: toastClassName,
      })
    },
  })
}

async function getAzureImportSettings(module: AzureImportModule, signal?: AbortSignal) {
  const res = await getAzureImportsApi(module).settings.$get({}, { init: { signal } })
  const { startDate } = await res.json()
  return { startDate: startDate ? parseISO(startDate) : null }
}

export const useAzureImportSettings = (module: AzureImportModule) => {
  return useQuery({
    queryKey: ['azure-import-settings', module],
    queryFn: async ({ signal }) => getAzureImportSettings(module, signal),
  })
}

async function updateAzureImportSettings(
  module: AzureImportModule,
  { startDate }: { startDate: Date | null },
) {
  await getAzureImportsApi(module).settings.$put({
    json: { startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null },
  })
}

export function useUpdateAzureImportSettings(module: AzureImportModule) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['uploads', 'common'])

  return useMutation({
    mutationKey: ['update-azure-import-settings', module],
    mutationFn: (variables: { startDate: Date | null }) =>
      updateAzureImportSettings(module, variables),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['azure-import-settings', module] })
    },
    onSuccess: () => {
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('azureImports.settings.saved'),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('azureImports.settings.saveFailed'),
        className: toastClassName,
      })
    },
  })
}

async function deleteAzureImport(module: AzureImportModule, id: number) {
  await getAzureImportsApi(module)[':id'].$delete({
    param: { id: String(id) },
  })
}

export function useDeleteAzureImport(module: AzureImportModule) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['uploads', 'common'])

  return useMutation({
    mutationKey: ['delete-azure-import', module],
    mutationFn: (id: number) => deleteAzureImport(module, id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['azure-imports'] })
    },
    onSuccess: () => {
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('azureImports.delete.deleted'),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('azureImports.delete.deleteFailed'),
        className: toastClassName,
      })
    },
  })
}
